import { IconInfoCircle } from '@tabler/icons-react';
import { BenefitStatus } from '@zorro/clients';
import { Alert } from '@zorro/design-system/v2/Alert';
import { Card } from '@zorro/design-system/v2/Card';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { Divider } from '@zorro/design-system/v2/Divider';
import { List } from '@zorro/design-system/v2/List';
import { Box, Group, Icon, Stack, Text } from '@zorro/web/design-system';
import {
  EnrollmentConfirmationFormFields,
  useAnalytics,
} from '@zorro/web/shared-ui';
import { Controller, UseFormReturn } from 'react-hook-form';

type Props = {
  sectionNumber: number;
  onboardingPeriodId: string;
  requiredActions: string[];
  benefitStatus?: BenefitStatus;
  enrollmentConfirmationForm: UseFormReturn<EnrollmentConfirmationFormFields>;
};

export const EnrollmentCommunicationSection = ({
  sectionNumber,
  onboardingPeriodId,
  requiredActions,
  benefitStatus,
  enrollmentConfirmationForm,
}: Props) => {
  const { sendEvent } = useAnalytics();
  const { control, watch } = enrollmentConfirmationForm;
  const hasRequiredActions = requiredActions.length > 0;
  const shouldSendEmail = watch('shouldSendEnrollmentConfirmationEmail');
  // A plan awaiting payment is confirmed but its confirmation email was held
  // back, so it does not get the "already sent" note of the paid statuses.
  const isEmailPossiblySent =
    benefitStatus === BenefitStatus.ACTIVE ||
    benefitStatus === BenefitStatus.ENROLLMENT_CONFIRMED ||
    benefitStatus === BenefitStatus.ENDED;
  const showEditNote = isEmailPossiblySent && shouldSendEmail;

  return (
    <>
      <Divider />
      <Stack mt="xl" gap="xl">
        <Text size="md" fw={600}>
          {sectionNumber}. Communication to employee
        </Text>
        <Stack gap="xl" ml="md">
          <Card
            variant="white-border"
            radius="md"
            p={0}
            style={{ overflow: 'hidden' }}
          >
            <Stack gap="md">
              <Box
                style={{
                  borderBottom: '1px solid var(--color-border-default-primary)',
                }}
              >
                <Controller
                  control={control}
                  name="shouldSendEnrollmentConfirmationEmail"
                  render={({ field: { value, onChange, ...rest } }) => (
                    <Checkbox
                      {...rest}
                      checked={Boolean(value)}
                      pl="xl"
                      pr="md"
                      py="sm"
                      label={
                        <Text fw={600} size="sm">
                          Send enrollment confirmation email to employee
                        </Text>
                      }
                      onChange={async (event) => {
                        const checked = event.currentTarget.checked;
                        onChange(checked);
                        if (checked) {
                          await sendEvent(
                            'send_enrollment_confirmation_email',
                            {
                              onboarding_period_id: onboardingPeriodId,
                            }
                          );
                        }
                      }}
                    />
                  )}
                />
              </Box>
              {hasRequiredActions && (
                <Stack gap="xxs" px="xl" pb={showEditNote ? 0 : 'md'}>
                  <Text size="sm">
                    This email will contain the following actions needed:
                  </Text>
                  <List type="ordered" size="sm">
                    {requiredActions.map((action) => (
                      <List.Item pl="xxs" key={action}>
                        {action}
                      </List.Item>
                    ))}
                  </List>
                </Stack>
              )}
              {showEditNote && (
                <Group
                  px="xl"
                  py="lg"
                  bg="rgba(204, 242, 251, 0.6)"
                  wrap="nowrap"
                  gap="xs"
                  align="center"
                  style={{
                    borderTop: '1px solid var(--color-border-default-primary)',
                  }}
                >
                  <Icon icon={IconInfoCircle} size={20} />
                  <Text size="sm">
                    Enrollment was already confirmed, and an email may have been
                    sent. Resend only if needed.
                  </Text>
                </Group>
              )}
            </Stack>
          </Card>
          <Alert variant="general-note">
            If carrier-confirmed details - such as premium, tax treatment, or
            coverage dates - differ from the employee&rsquo;s original election,
            send a personal email informing them of the changes
          </Alert>
        </Stack>
      </Stack>
    </>
  );
};
